import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Award, Linkedin, Mail, Pencil, Phone } from "lucide-react";
import { profileApi } from "@/api/profile";
import { Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { RoleChip } from "@/components/shared/RoleChip";
import { useAuth } from "@/hooks/useAuth";
import { PhotoDisplay } from "./ProfileEditPage";

export function ProfileViewPage() {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  // "me" is a client-side alias so /account/profile always points at the
  // signed-in user without needing to embed their id in the URL.
  const targetId = !userId || userId === "me" ? (user?.id ?? "") : userId;
  const isMe = targetId === user?.id;

  const profile = useQuery({
    queryKey: ["profile", targetId],
    queryFn: () => profileApi.get(targetId),
    enabled: !!targetId,
  });

  if (profile.isLoading) {
    return <div className="text-caption uppercase tracking-wide text-gray-mid">Loading profile…</div>;
  }
  if (profile.isError || !profile.data) {
    return (
      <div className="max-w-md mx-auto text-center py-space-5">
        <p className="text-small text-gray-mid">This profile hasn't been set up yet.</p>
        {isMe && (
          <Link to="/account/profile/edit" className="inline-block mt-space-3">
            <Button>Create your profile</Button>
          </Link>
        )}
      </div>
    );
  }

  const p = profile.data;
  return (
    <>
      <PageHeader
        title="Profile"
        actions={
          isMe ? (
            <Link to="/account/profile/edit">
              <Button variant="secondary">
                <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
              </Button>
            </Link>
          ) : null
        }
      />

      <div className="max-w-[800px] mx-auto grid gap-space-5">
        <section className="flex flex-col items-center gap-space-3 py-space-4">
          <PhotoDisplay src={p.photoUrl} name={p.displayName} size={120} />
          <div className="text-center">
            <h2 className="font-display font-bold text-h2 text-onyx m-0">{p.displayName}</h2>
            {p.jobTitle && <div className="text-body text-gray-dark mt-space-1">{p.jobTitle}</div>}
            {p.department && <div className="text-caption text-gray-mid">{p.department}</div>}
            {p.roleCodes.length > 0 && (
              <div className="flex items-center justify-center gap-space-1 mt-space-2 flex-wrap">
                {p.roleCodes.map((r) => <RoleChip key={r} code={r} />)}
              </div>
            )}
          </div>
        </section>

        {p.bio && (
          <section>
            <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">About</h3>
            <p className="text-body text-gray-dark leading-normal whitespace-pre-line">{p.bio}</p>
          </section>
        )}

        <section>
          <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">Contact</h3>
          <ul className="grid gap-space-2 text-small text-gray-dark">
            <ContactRow icon={Mail} label="Email" value={p.email} href={`mailto:${p.email}`} />
            {p.phoneNumber && <ContactRow icon={Phone} label="Phone" value={p.phoneNumber} href={`tel:${p.phoneNumber}`} />}
            {p.linkedInUrl && <ContactRow icon={Linkedin} label="LinkedIn" value={p.linkedInUrl} href={p.linkedInUrl} external />}
          </ul>
        </section>

        {p.certifications && (
          <section>
            <h3 className="font-display font-bold text-h4 text-onyx mb-space-2">
              <Award size={16} strokeWidth={1.5} aria-hidden className="inline -mt-1 mr-1 text-gold-shadow" />
              Certifications
            </h3>
            <p className="text-body text-gray-dark">{p.certifications}</p>
          </section>
        )}
      </div>
    </>
  );
}

function ContactRow({
  icon: Icon, label, value, href, external,
}: {
  icon: React.ComponentType<{ size: number; strokeWidth: number; "aria-hidden": true; className?: string }>;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}) {
  const body = (
    <span className="flex items-center gap-space-2">
      <Icon size={14} strokeWidth={1.5} aria-hidden className="text-gold-shadow" />
      <span className="text-caption uppercase tracking-wide text-gray-mid w-20">{label}</span>
      <span>{value}</span>
    </span>
  );
  return (
    <li>
      {href ? (
        <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}
           className="hover:text-gold-shadow">
          {body}
        </a>
      ) : body}
    </li>
  );
}
